import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  ChevronRight,
  Command,
  Cpu,
  LayoutDashboard,
  LogOut,
  PanelLeft,
  Router,
  Cable,
  Settings,
  User,
  Users,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { PATHS } from '@/constants/routes';
import { cn } from '@/lib/utils';
import {
  Sidebar as AppSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const navMain = [
  {
    title: 'Dashboard',
    url: PATHS.DASHBOARD,
    icon: LayoutDashboard,
  },
  {
    title: 'Catalog',
    icon: Router,
    items: [
      { title: 'MQTT Configurations', url: PATHS.MQTT, icon: Cable },
      { title: 'Gateway Types', url: PATHS.GATEWAY_TYPES, icon: Router },
      { title: 'Device Types', url: PATHS.DEVICE_TYPES, icon: Cpu },
    ],
  },
];

const navSecondary = [
  {
    title: 'Admin Management',
    url: PATHS.USERS,
    icon: Users,
  },
  {
    title: 'Settings',
    url: PATHS.SETTINGS,
    icon: Settings,
  },
];

interface SidebarProps {
  collapsible?: 'offcanvas' | 'icon' | 'none';
}

export const Sidebar = ({ collapsible = 'icon' }: SidebarProps): JSX.Element => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { isMobile, state, toggleSidebar } = useSidebar();

  const initials = React.useMemo(() => {
    const name = user?.fullName || user?.email || 'Admin';
    return name.substring(0, 2).toUpperCase();
  }, [user]);

  const displayName = user?.fullName || user?.email?.split('@')[0] || 'Admin';

  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navMain.forEach((item) => {
      if (item.items?.some((sub) => location.pathname.startsWith(sub.url))) {
        initial[item.title] = true;
      }
    });
    return initial;
  });

  React.useEffect(() => {
    navMain.forEach((item) => {
      if (item.items?.some((sub) => location.pathname.startsWith(sub.url))) {
        setOpenSections((prev) => ({ ...prev, [item.title]: true }));
      }
    });
  }, [location.pathname]);

  return (
    <AppSidebar collapsible={collapsible} variant="floating">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="w-full">
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent">
              <Link to={PATHS.DASHBOARD}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                  <Command className="size-4" />
                </div>
                {state !== 'collapsed' && (
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-bold tracking-tight text-primary">IOTPOT</span>
                    <span className="truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                      Admin Console
                    </span>
                  </div>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Control Plane</SidebarGroupLabel>
          <SidebarMenu>
            {navMain.map((item) => {
              const hasSubItems = !!item.items?.length;
              const isActive = item.url
                ? location.pathname.startsWith(item.url)
                : item.items?.some((sub) => location.pathname.startsWith(sub.url));

              if (!hasSubItems) {
                return (
                  <SidebarMenuItem className="w-full" key={item.title}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      isActive={isActive}
                      className="w-full transition-all data-[active=true]:bg-primary/10 data-[active=true]:font-medium data-[active=true]:text-primary"
                    >
                      <Link to={item.url!}>
                        <item.icon className="group-data-[active=true]:text-primary" />
                        <span className={cn('flex-1', state === 'collapsed' && 'hidden')}>
                          {item.title}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }

              if (state === 'collapsed') {
                return (
                  <SidebarMenuItem className="w-full" key={item.title}>
                    <CollapsedDropdown item={item} pathname={location.pathname} />
                  </SidebarMenuItem>
                );
              }

              return (
                <Collapsible
                  key={item.title}
                  open={openSections[item.title] || false}
                  onOpenChange={(open) =>
                    setOpenSections((prev) => ({ ...prev, [item.title]: open }))
                  }
                  className="group/collapsible w-full"
                >
                  <SidebarMenuItem className="w-full">
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={isActive}
                        className="w-full transition-all data-[active=true]:bg-primary/10 data-[active=true]:font-medium data-[active=true]:text-primary"
                      >
                        <item.icon className="group-data-[active=true]:text-primary" />
                        <span className="flex-1">{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname.startsWith(subItem.url)}
                            >
                              <Link to={subItem.url}>
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup className={cn('mt-auto', state === 'collapsed' && 'items-center')}>
          {state !== 'collapsed' && <SidebarGroupLabel>Administration</SidebarGroupLabel>}
          <SidebarMenu>
            {navSecondary.map((item) => (
              <SidebarMenuItem className="w-full" key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={location.pathname.startsWith(item.url)}
                  className="w-full transition-all data-[active=true]:bg-primary/10 data-[active=true]:font-medium data-[active=true]:text-primary"
                >
                  <Link to={item.url}>
                    <item.icon className="group-data-[active=true]:text-primary" />
                    <span className={cn('flex-1', state === 'collapsed' && 'hidden')}>
                      {item.title}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarMenu>
          <SidebarMenuItem className={cn('mb-4', state === 'collapsed' && 'mb-0')}>
            <SidebarMenuButton
              onClick={toggleSidebar}
              className="w-full font-medium text-muted-foreground transition-all hover:text-primary"
              tooltip="Toggle Sidebar"
            >
              <PanelLeft className="size-4 text-muted-foreground transition-colors group-hover:text-primary" />
              <span className={cn('ml-2', state === 'collapsed' && 'hidden')}>
                Collapse sidebar
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="w-full">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="w-full data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    {user?.avatar ? <AvatarImage src={user.avatar} alt={displayName} /> : null}
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  {state !== 'collapsed' && (
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{displayName}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user?.email || 'admin@example.com'}
                      </span>
                    </div>
                  )}
                  {state !== 'collapsed' && <ChevronDown className="ml-auto size-4" />}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Account</DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link to={PATHS.PROFILE}>
                      <User className="mr-2 size-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={PATHS.NOTIFICATIONS}>
                      <Bell className="mr-2 size-4" />
                      Notifications
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    void logout();
                  }}
                >
                  <LogOut className="mr-2 size-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </AppSidebar>
  );
};

function CollapsedDropdown({ item, pathname }: { item: any; pathname: string }) {
  const [open, setOpen] = React.useState(false);
  const isActive = item.url
    ? pathname.startsWith(item.url)
    : item.items?.some((sub: any) => pathname.startsWith(sub.url));

  return (
    <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            isActive={isActive}
            className="w-full transition-all data-[active=true]:bg-primary/10 data-[active=true]:font-medium data-[active=true]:text-primary"
          >
            {item.icon && <item.icon className="group-data-[active=true]:text-primary" />}
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" className="w-48">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {item.items?.map((subItem: any) => (
              <DropdownMenuItem
                key={subItem.title}
                asChild
                className={pathname.startsWith(subItem.url) ? 'bg-accent text-accent-foreground' : ''}
                onClick={() => setOpen(false)}
              >
                <Link to={subItem.url}>{subItem.title}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default Sidebar;
